// src/Pages/finanzas-clientes/useLavadosSection.ts
//
// Round 2026-06-22 (AC7) — la pestaña "Lavandería" de /finanzas-cliente muestra
// SOLO los lavados ALEDAÑOS que se le cobran al cliente: lavados sueltos (sin
// vínculo a un arriendo) que el cliente pidió y paga (pagado_por.tipo=1). Los
// lavados de un arriendo NO aparecen acá porque su costo ya va dentro de la
// limpieza que se le descuenta en la liquidación (sería doble cobro). Consume
// el endpoint dedicado `GET /finanzas/lavados/:id`. Regla canónica:
// docs/flujo-financiero.md §7.6. Espejo de useAseosSection.
import { useCallback, useEffect, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";

// Fila de lavado tal como la devuelve el backend para el cliente.
export type LavadoRow = {
  id: number;
  fecha: string | null;
  descripcion: string | null;
  cantidad_lavados: number;
  cantidad_secados: number;
  monto: number;
  propiedad_id: number | null;
  // Quién paga: el endpoint ya garantiza { tipo: 1 } (Cliente).
  pagado_por: { tipo: number; nombre?: string | null } | null;
  // Efecto en el bolsillo del cliente: siempre egreso (él paga estos lavados).
  efectoCliente: number;
};

// Subtotal del período: estos lavados solo pueden ser egreso para el cliente
// (nunca ingreso) → misma forma `{egresoCliente}` que aseos.
export type LavadoTotals = {
  egresoCliente?: number;
  currency?: string;
};

export function useLavadosSection(
  clientId: number | null | undefined,
  selectedPropId: number | null,
) {
  const [rows, setRows] = useState<LavadoRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState<LavadoTotals | null>(null);
  const [month, setMonth] = useState<Dayjs | null>(null);

  const fetchRows = useCallback(
    async (p = page, s = size) => {
      if (!clientId || !selectedPropId) return;
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(p));
        params.set("size", String(s));
        params.set("propId", String(selectedPropId));
        if (month) {
          params.set("from", month.startOf("month").toISOString());
          params.set("to", month.endOf("month").toISOString());
        }

        const json = await apiFetch<any>(
          `/finanzas/lavados/${clientId}?${params.toString()}`,
        );
        if (!json) throw new Error("No se pudieron obtener los lavados");

        setRows(json.data || []);
        setTotal(json.total || 0);
        setPage(json.page || 1);
        setSize(json.size || 20);
        setTotals(json.totals || null);
      } catch (e: any) {
        getMsg()?.error(e?.message || "Error cargando lavandería");
      } finally {
        setLoading(false);
      }
    },
    [clientId, selectedPropId, page, size, month],
  );

  useEffect(() => {
    if (clientId && selectedPropId) fetchRows(1, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedPropId, month]);

  return {
    rows,
    total,
    page,
    size,
    loading,
    totals,
    month,
    setMonth,
    onPaginate: (p: number, s?: number) => fetchRows(p, s ?? size),
    refetch: fetchRows,
  };
}
